/**
 * M3X.WORKSPACE-CLOSURE A4 — WorkspaceTopBar with the chat sparkle button.
 *
 * Closes Amer's screenshot complaint #2 — the ⌘/ chat indicator was
 * missing from the top bar. Now: the AI status sparkle button doubles
 * as the chat toggle (matches the legacy AppShell pattern). ⌘/
 * keybind is also re-wired in WorkspaceShell so the panel responds
 * to keyboard as before.
 *
 * Slim layout: brand wordmark · breadcrumb · action cluster (chat ·
 * notifications · language · user).
 */

import { UserButton } from "@clerk/clerk-react";
import {
  Bell,
  Home,
  Languages,
  MessageSquareText,
  Search,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { AiStatusButton } from "../components/AiStatusButton";
import { LegalEntitySwitcher } from "../components/LegalEntitySwitcher";
import { Breadcrumb } from "./Breadcrumb";
import type { Screen, SubDomain } from "../navigation/registry";


// M3X.WORKSPACE-CLOSURE Session 2.5 PR 3 — Solvait-branded Clerk
// appearance. Hides Clerk attribution + dev-mode badge; brand-
// gradient avatar; brand colors throughout the popover.
const _SOLVAIT_USER_BUTTON_APPEARANCE = {
  variables: {
    colorPrimary: "#2260C9",
    colorText: "#1e293b",
    colorTextSecondary: "#64748b",
    colorBackground: "#ffffff",
    colorInputBackground: "#f8fafc",
    colorInputText: "#1e293b",
    borderRadius: "8px",
    fontFamily: '"Comfortaa", system-ui, sans-serif',
    fontFamilyButtons: '"Raleway", system-ui, sans-serif',
  },
  elements: {
    userButtonAvatarBox: {
      width: "32px",
      height: "32px",
      background: "linear-gradient(135deg, #00D3C2, #A846BF)",
    },
    userButtonPopoverCard: {
      border: "1px solid #e2e8f0",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
      borderRadius: "10px",
    },
    userButtonPopoverActionButton: {
      color: "#475569",
      "&:hover": {
        background: "#f1f5f9",
        color: "#1e293b",
      },
    },
    userButtonPopoverActionButtonIcon: {
      color: "#64748b",
    },
    userButtonPopoverFooter: {
      // Hide "Secured by Clerk"
      display: "none",
    },
    badge: {
      // Hide "Development mode" tag
      display: "none",
    },
  },
} as const;


export interface WorkspaceTopBarProps {
  currentModule: string | null;
  currentSubDomain: SubDomain | null;
  currentScreen: Screen | null;
  locale: "en" | "ar";
  onLocaleToggle?: () => void;
  /** Toggles the M22-M25 chat panel. */
  onChatToggle?: () => void;
  /** Drives the active styling on the chat button when the panel is open. */
  chatOpen?: boolean;
  /**
   * M3X.WORKSPACE-CLOSURE Session 2.7 PR 1 — opens the ⌘K command
   * palette (GlobalSearch). The search input in the top bar fires
   * this when clicked.
   */
  onSearchOpen?: () => void;
}


export function WorkspaceTopBar({
  currentModule,
  currentSubDomain,
  currentScreen,
  locale,
  onLocaleToggle,
  onChatToggle,
  chatOpen = false,
  onSearchOpen,
}: WorkspaceTopBarProps) {
  const { t } = useTranslation();
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const cmdKey = isMac ? "⌘" : "Ctrl";

  return (
    <div
      data-topbar
      className="flex h-14 w-full items-center border-b border-border-default bg-surface-default"
      style={{
        // M3X.WORKSPACE-CLOSURE Session 2.9 PR 1 — locked padding so the
        // right cluster sits flush against the viewport edge with the
        // spec-mandated 20px gap.
        paddingInlineStart: "16px",
        paddingInlineEnd: "20px",
      }}
    >
      {/* Brand wordmark — LTR always (proper noun) */}
      <div
        className="font-bold tracking-tight text-text-primary"
        style={{ direction: "ltr", marginInlineEnd: "20px" }}
      >
        <span className="bg-brand-gradient-h bg-clip-text text-transparent">
          Solvait
        </span>
      </div>

      {/* Breadcrumb — adjacent to wordmark, max 30vw before ellipsis */}
      <div
        data-topbar-breadcrumb
        className="truncate"
        style={{ maxWidth: "30vw", marginInlineEnd: "12px" }}
      >
        <Breadcrumb
          currentModule={currentModule}
          currentSubDomain={currentSubDomain}
          currentScreen={currentScreen}
          locale={locale}
        />
      </div>

      {/* M3X.WORKSPACE-CLOSURE FINAL PR 2 — Legal Entity switcher pill
          (M26.5 PR 2 + M27 PR 3 component). Single-LE tenant renders
          a read-only pill; multi-LE renders a popover. */}
      <div data-topbar-le-switcher>
        <LegalEntitySwitcher />
      </div>

      {/* FINAL PR 2 — left flex spacer (centers the search) */}
      <div className="flex-1" data-topbar-spacer-left />

      {/* M3X.WORKSPACE-CLOSURE FINAL PR 2 — search input dead-centered
          via dual flex spacers, widened 320→480px, border-radius 8→10px. */}
      <button
        type="button"
        onClick={onSearchOpen}
        data-topbar-search
        aria-label={t(
          "workspace.search.placeholder",
          "Search anything, run any action…",
        )}
        className="group/topbar-search relative flex items-center transition-colors"
        style={{
          // Locked tight per Amer's 2026-05-30 review:
          //   - basis 320px (was 480px; the old size visually dominated
          //     the bar at typical 1440-1920 widths)
          //   - cap at min(320px, 28vw) so narrow viewports shrink
          //     gracefully without the right cluster overflowing
          //   - min 200px so it doesn't collapse to a tiny pill on
          //     mobile + tablet
          //   - explicit height 32px (was h-9 / 36px) with min/max so
          //     no Tailwind / browser default can inflate it
          flex: "0 1 320px",
          minWidth: "200px",
          maxWidth: "min(320px, 28vw)",
          height: "32px",
          minHeight: "32px",
          maxHeight: "32px",
          backgroundColor: "#f1f5f9",
          borderRadius: "8px",
          boxSizing: "border-box",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = "#e2e8f0";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = "#f1f5f9";
        }}
      >
        <Search
          aria-hidden
          className="absolute h-3.5 w-3.5"
          style={{
            insetInlineStart: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--text-tertiary)",
          }}
        />
        <span
          className="flex-1 truncate text-start"
          style={{
            color: "var(--text-tertiary)",
            fontSize: "13px",
            paddingInlineStart: "36px",
            paddingInlineEnd: "8px",
          }}
        >
          {t(
            "workspace.search.placeholder",
            "Search anything, run any action…",
          )}
        </span>
        <kbd
          aria-hidden
          className="inline-block"
          style={{
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            padding: "1px 5px",
            borderRadius: "3px",
            fontSize: "10px",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            color: "#64748b",
            marginInlineEnd: "8px",
          }}
        >
          {cmdKey}K
        </kbd>
      </button>

      {/* FINAL PR 2 — right flex spacer (pushes the right cluster
          flush to the viewport edge). The search ends up dead-centered
          between the two spacers. */}
      <div className="flex-1" data-topbar-spacer-right />

      {/* Right cluster — flush right per S2.9 PR 1 + FINAL PR 2 */}
      <div
        data-topbar-right-cluster
        className="flex items-center"
        style={{ gap: "8px" }}
      >
        {/* FINAL PR 2 — AI status pill restored. M21.10 component
            with brand-teal sparkle + autonomy mode + pause/popover. */}
        <div data-topbar-ai-status>
          <AiStatusButton />
        </div>
        {/* FINAL PR 2 spec order: AI · Bell · Lang · Chat · Avatar */}
        <button
          type="button"
          aria-label={t("nav.notifications", "Notifications")}
          className="rounded-md p-2 text-text-tertiary hover:bg-surface-subtle hover:text-text-primary"
        >
          <Bell className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={t("nav.language", "Toggle language")}
          onClick={onLocaleToggle}
          className="rounded-md p-2 text-text-tertiary hover:bg-surface-subtle hover:text-text-primary"
        >
          <Languages className="h-4 w-4" />
        </button>
        {/* Chat / AI panel toggle — brand AI mark; ⌘/ also toggles. */}
        <button
          type="button"
          aria-label={t("nav.chat", "Open AI chat (⌘/)")}
          aria-pressed={chatOpen}
          onClick={onChatToggle}
          data-chat-toggle
          className={[
            "relative rounded-md p-2 transition-colors",
            chatOpen
              ? "bg-ai-bg text-ai-deep"
              : "text-text-tertiary hover:bg-surface-subtle hover:text-text-primary",
          ].join(" ")}
        >
          <Sparkles className="h-4 w-4 text-ai-accent" />
          {chatOpen && (
            <MessageSquareText
              aria-hidden
              className="absolute -bottom-0.5 -end-0.5 h-2.5 w-2.5 text-ai-deep"
            />
          )}
        </button>
        <UserButton
          afterSignOutUrl="/"
          appearance={_SOLVAIT_USER_BUTTON_APPEARANCE}
        >
          <UserButton.MenuItems>
            {/* M3X.WORKSPACE-CLOSURE Session 2.5 PR 3 — "My Hub" link
                surfaced at the top of the avatar dropdown. */}
            <UserButton.Link
              label={t("workspace.me.myHub", "My Hub")}
              labelIcon={<Home size={16} />}
              href="/me"
            />
          </UserButton.MenuItems>
        </UserButton>
      </div>
    </div>
  );
}


export default WorkspaceTopBar;
